import { useAccountStorage, useOrganizationStorage } from '../../../../../../../AppClients';
import { requireAnyOrgPermission, requireAnySystemPermission, requireOrgPermission } from '../../../../../../../backend/features/permissions/shared';
import { OrganizationSlideout } from '../../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationSlideout';
import { OrganizationWithExceededLimitConfirmDialog } from '../../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationWithExceededLimitConfirmDialog';
import { getOrgWithExceededLimit } from '../../../../../../../ui/features/organizations/components/OrganizationsPanel/utils';
import { showOrganizationTreeDialog } from '../../../../../../../ui/features/organizations/components/OrganizationsTreeDialog';
import { useContactManagementTranslation, useUserState } from '../../../../../../../ui/hooks';
import { DataTable, useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { handleErrorDialog, showAlertDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { usePlatformLoader } from '@juvare/platform-sdk-react/ui/hooks';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box } from '@mui/material';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useAccountOrgMembershipTable } from '../../../../hooks';
import { AccountMembershipTableContent } from './components/AccountMembershipTableContent';
import { AccountMembershipTableHeader } from './components/AccountMembershipTableHeader';
export const AccountMembershipTable = (props) => {
    var _a, _b, _c, _d;
    const { t } = useContactManagementTranslation();
    const userState = useUserState();
    const { onStatelessReload } = usePlatformLoader({});
    const accountStorage = useAccountStorage();
    const organizationStorage = useOrganizationStorage();
    const settings = useSelector(getSettings);
    const location = useLocation();
    const { orgRoleMap, orgStatusMap } = settings;
    const userHasSystemPermissions = requireAnySystemPermission(userState, 'manage-accounts') && requireAnySystemPermission(userState, 'manage-accounts-organizations');
    const userHasOrgPermissions = requireAnyOrgPermission(userState, 'manage-members');
    const [slideoutOrgId, setSlideoutOrgId] = React.useState();
    const userCanDoBulkActions = requireAnySystemPermission(userState, 'all-organizations-admin');
    const handleToggleSlideout = (membershipId) => {
        setSlideoutOrgId(membershipId);
    };
    const getDefaultRole = () => {
        var _a;
        const [defaultRole] = (_a = Object.entries(orgRoleMap).find(([, role]) => !!role.default)) !== null && _a !== void 0 ? _a : [];
        return defaultRole;
    };
    const { dataTableProps, reload } = useAccountOrgMembershipTable({
        accountId: props.accountId,
        userCanDoBulkActions: userCanDoBulkActions,
        useDataTableFunction: useDataTableUsingUrl,
    });
    const organizationIds = Array.from(new Set([
        ...Object.keys((_b = (_a = dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.orgRole) !== null && _b !== void 0 ? _b : {}),
        ...Object.keys((_d = (_c = dataTableProps.customBackendResponseData) === null || _c === void 0 ? void 0 : _c.orgStatus) !== null && _d !== void 0 ? _d : {}),
    ]));
    const handleAddMemberships = (newOrganizationIds, orgTree) => {
        let uniqueNewOrganizationIds = Array.from(new Set([...newOrganizationIds, ...organizationIds]));
        // newOrganizationIds can have duplicates if organization has network parents. So, removing duplicates before checking equality
        if (isEqual(uniqueNewOrganizationIds.sort(), organizationIds.sort())) {
            return;
        }
        function handleUpdateMembership() {
            var _a, _b, _c;
            const newRoleData = Object.assign({}, (_a = dataTableProps === null || dataTableProps === void 0 ? void 0 : dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.orgRole);
            const newStatusData = Object.assign({}, (_b = dataTableProps === null || dataTableProps === void 0 ? void 0 : dataTableProps.customBackendResponseData) === null || _b === void 0 ? void 0 : _b.orgStatus);
            // remove orgIds from newData if user does not have permission
            if (!userHasSystemPermissions) {
                for (const orgId of organizationIds) {
                    if (!userState.orgMembership.includes(orgId) && !requireOrgPermission(userState, orgId, 'manage-members')) {
                        delete newRoleData[orgId];
                        delete newStatusData[orgId];
                    }
                }
            }
            for (const orgId of uniqueNewOrganizationIds) {
                if (!organizationIds.includes(orgId)) {
                    if (requireOrgPermission(userState, orgId, 'manage-members') || userHasSystemPermissions) {
                        newRoleData[orgId] = (_c = getDefaultRole()) !== null && _c !== void 0 ? _c : 'user'; // TODO need to make 'user' and 'admin' as required roles, meaning they must always exist
                        newStatusData[orgId] = 'accepted';
                    }
                    else if (props.accountId === (userState === null || userState === void 0 ? void 0 : userState.id)) {
                        newStatusData[orgId] = 'pending';
                    }
                }
            }
            accountStorage
                .post({
                action: 'partial-update',
                id: props.accountId,
                data: { orgRole: newRoleData, orgStatus: newStatusData },
                auditTags: props.auditTags,
            })
                .then(() => {
                if ((userState === null || userState === void 0 ? void 0 : userState.id) === props.accountId) {
                    onStatelessReload().then().catch(handleErrorSnackbar);
                }
                reload();
            })
                .catch(handleErrorDialog);
        }
        if (!settings.accountMembershipLimitPerOrgSubTreeEnabled || !orgTree) {
            handleUpdateMembership();
            return;
        }
        organizationStorage
            .list({ filter: [{ id: uniqueNewOrganizationIds, op: 'in' }] })
            .then((data) => {
            const orgWithExceededLimit = getOrgWithExceededLimit(orgTree, data, organizationIds);
            const orgWithExceededLimitArr = orgWithExceededLimit
                ? Array.from(orgWithExceededLimit.entries()).map(([id, name]) => ({ id, name }))
                : undefined;
            if (!(orgWithExceededLimitArr === null || orgWithExceededLimitArr === void 0 ? void 0 : orgWithExceededLimitArr.length)) {
                handleUpdateMembership();
                return;
            }
            showConfirmDialog({
                title: t('publicSelfRegistration.membership.accountCantBeAdded'),
                content: React.createElement(OrganizationWithExceededLimitConfirmDialog, { orgWithExceededLimit: orgWithExceededLimitArr }),
                confirmLabel: t('common.continue'),
                onConfirm: () => {
                    uniqueNewOrganizationIds = uniqueNewOrganizationIds.filter((uniqueId) => !(orgWithExceededLimit === null || orgWithExceededLimit === void 0 ? void 0 : orgWithExceededLimit.has(uniqueId)));
                    handleUpdateMembership();
                },
            });
        })
            .catch(handleErrorDialog);
    };
    const handleUpdateMembership = ({ orgId, role, status, onSubmitSuccess, }) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const newRoleData = Object.assign({}, (_a = dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.orgRole);
        const newStatusData = Object.assign({}, (_b = dataTableProps.customBackendResponseData) === null || _b === void 0 ? void 0 : _b.orgStatus);
        // remove orgIds from newData if user does not have permission
        if (!userHasSystemPermissions) {
            for (const orgId of organizationIds) {
                if (!userState.orgMembership.includes(orgId) && !requireOrgPermission(userState, orgId, 'manage-members')) {
                    delete newRoleData[orgId];
                    delete newStatusData[orgId];
                }
            }
        }
        if (status) {
            if (role && ((_d = (_c = dataTableProps.customBackendResponseData) === null || _c === void 0 ? void 0 : _c.orgRole) === null || _d === void 0 ? void 0 : _d[orgId]) !== role && !((_e = orgStatusMap[status]) === null || _e === void 0 ? void 0 : _e.requireOrgRole)) {
                const applicableStatuses = Object.values(orgStatusMap)
                    .filter((s) => s.requireOrgRole)
                    .map((s) => `"${s.label}"`);
                showAlertDialog({
                    title: t('forms.orgMembers.cannotChangeRole', {
                        statuses: applicableStatuses.join(', '),
                        count: applicableStatuses.length,
                    }),
                });
                return;
            }
        }
        if (role) {
            newRoleData[orgId] = role;
        }
        else {
            delete newRoleData[orgId];
        }
        if (status) {
            newStatusData[orgId] = status;
        }
        else {
            delete newStatusData[orgId];
        }
        if (status && ((_g = (_f = dataTableProps.customBackendResponseData) === null || _f === void 0 ? void 0 : _f.orgStatus) === null || _g === void 0 ? void 0 : _g[orgId]) !== status) {
            if ((_h = orgStatusMap[status]) === null || _h === void 0 ? void 0 : _h.requireOrgRole) {
                if (!((_k = (_j = dataTableProps.customBackendResponseData) === null || _j === void 0 ? void 0 : _j.orgRole) === null || _k === void 0 ? void 0 : _k[orgId]) && !role) {
                    newRoleData[orgId] = (_l = role !== null && role !== void 0 ? role : getDefaultRole()) !== null && _l !== void 0 ? _l : 'user';
                }
            }
            else {
                delete newRoleData[orgId];
            }
        }
        if (!requireAnySystemPermission(userState, 'manage-accounts') &&
            Object.keys(newRoleData).length === 0 &&
            Object.keys(newStatusData).length === 0) {
            handleErrorDialog(t('forms.accountMembershipUser.mustSelectMembership'));
            return;
        }
        accountStorage
            .post({
            action: 'partial-update',
            id: props.accountId,
            data: { orgRole: newRoleData, orgStatus: newStatusData },
            auditTags: props.auditTags,
        })
            .then(() => {
            if ((userState === null || userState === void 0 ? void 0 : userState.id) === props.accountId) {
                onStatelessReload().then().catch(handleErrorSnackbar);
            }
            reload();
            onSubmitSuccess === null || onSubmitSuccess === void 0 ? void 0 : onSubmitSuccess();
        })
            .catch(handleErrorDialog);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DataTable, Object.assign({}, dataTableProps, { secondaryAction: {
                icon: userHasSystemPermissions || userHasOrgPermissions ? React.createElement(AddIcon, null) : React.createElement(SearchIcon, null),
                label: userHasSystemPermissions || userHasOrgPermissions
                    ? t('panels.organizations.addOrgs')
                    : t('panels.accounts.membership.findOrganizations'),
                variant: 'outlined',
                'data-test': 'add-organizations-button',
                onClick: () => {
                    showOrganizationTreeDialog({
                        selectButtonLabel: userHasSystemPermissions || userHasOrgPermissions
                            ? t('panels.organizations.addOrgs')
                            : t('panels.accounts.membership.joinOrganizations'),
                        showSelectAllChildren: true,
                        multiSelect: true,
                        initialSelectedOrganizationIds: organizationIds,
                        staticFilters: props.staticDialogFilters,
                        onChange: handleAddMemberships,
                    });
                },
            }, hideHeader: true, customHeaderElement: userCanDoBulkActions && (React.createElement(AccountMembershipTableHeader, { canManageMemberships: userHasOrgPermissions || userHasSystemPermissions, onSelectAllRowsInCurrentPageClick: dataTableProps.onSelectAllRowsInCurrentPageClick, onDeselectAllRows: dataTableProps.onDeselectAllRows, rows: dataTableProps.rows, selectedRowIds: dataTableProps.selectedRowIds })), customTableSurfaceComponent: (props) => React.createElement(Box, { sx: { backgroundColor: themeColors.surface.secondary } }, props.children), customTableContentComponent: (tableContentProps) => {
                var _a;
                return (React.createElement(AccountMembershipTableContent, Object.assign({ memberFullName: (_a = dataTableProps.customBackendResponseData) === null || _a === void 0 ? void 0 : _a.memberFullName, onUpdateMembership: handleUpdateMembership, handleSlideoutOrgId: handleToggleSlideout }, tableContentProps, props)));
            }, title: t('panels.organizations.title') })),
        React.createElement(OrganizationSlideout, { onResolveNavigationPathname: (section) => {
                const basePath = location.pathname.replace(/\/accounts.*$/, '');
                return `${basePath}/organizations/${slideoutOrgId}/${section.toLowerCase()}`;
            }, onClose: () => handleToggleSlideout(), onReload: reload, organizationId: slideoutOrgId })));
};
